import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;
const TOKEN_KEY = 'nrg-token';

// Helper: fetch with Authorization header
export async function authFetch(url, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (token) {
    headers['Authorization'] = 'Bearer ' + token;
  }
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    // Token expired or invalid — clear it
    localStorage.removeItem(TOKEN_KEY);
  }
  return res;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // On mount: check for existing token and validate it
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    authFetch(`${API}/api/auth/me`)
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error('Invalid token');
      })
      .then((data) => {
        setUser(data.user || data);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || 'Invalid credentials or account inactive' };
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Cannot reach server. Is the backend running?' };
    }
  }, []);

  const logout = useCallback(() => {
    fetch(`${API}/api/auth/logout`, { method: 'POST' }).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setUsers([]);
  }, []);

  const register = useCallback(async (data) => {
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          name: data.name,
          org: data.org || 'CENEC',
        }),
      });
      const result = await res.json();
      if (!res.ok) {
        return { success: false, error: result.error || 'Registration failed' };
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Cannot reach server. Is the backend running?' };
    }
  }, []);

  const getUsers = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/users`);
      if (!res.ok) return [];
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.users || []);
      setUsers(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  const updateUser = useCallback(async (id, updates) => {
    try {
      const res = await authFetch(`${API}/api/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      if (!res.ok) return false;
      const data = await res.json();
      // If the current user was updated, refresh their state
      if (user?.id === id) {
        setUser(data.user || data);
      }
      return true;
    } catch {
      return false;
    }
  }, [user]);

  const deleteUser = useCallback(async (id) => {
    if (id === user?.id) return false;
    try {
      const res = await authFetch(`${API}/api/users/${id}`, {
        method: 'DELETE',
      });
      return res.ok;
    } catch {
      return false;
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{ user, users, loading, login, logout, register, updateUser, deleteUser, getUsers }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
